import React, { useState, useMemo, useCallback, useEffect } from 'react'

import { useOperatorData } from '@contexts/operator/OperatorDataContext'
import { useIsStandard } from '@contexts/operator/PlanContext'
import { useOperatorNotification } from '@contexts/operator/OperatorNotificationContext'
import { HideInStandard } from '@components/operator/shared/StandardGate'
import ListPageHeader from '@shared/ListPageHeader'
import LocationSnapshotConceptModal from '@shared/location_snapshots/LocationSnapshotConceptModal'
import { DatePicker, Select, Button, Table, Card, Space, Statistic, Row, Col, message, Tooltip } from 'antd'
import { DownloadOutlined, SearchOutlined, QuestionCircleFilled } from '@ant-design/icons'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import { locationSnapshotService } from '@services/operator/locationSnapshotService'

const { RangePicker } = DatePicker
const LINE_COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16']

export default function OperatorLocationSnapshotsReport() {
  const { merchants, merchantId: ctxMerchantId } = useOperatorData()
  const isStandard = useIsStandard()
  const { addJob } = useOperatorNotification()
  const { t, i18n } = useTranslation()

  const [dateRange, setDateRange] = useState(null)
  const [merchantId, setMerchantId] = useState(null)
  const [summaryData, setSummaryData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [conceptModalVisible, setConceptModalVisible] = useState(false)

  // 單一 merchant 模式下，自動鎖定為 context 提供的 merchant
  useEffect(() => {
    if (isStandard && ctxMerchantId && merchantId !== ctxMerchantId) {
      setMerchantId(ctxMerchantId)
    }
  }, [isStandard, ctxMerchantId, merchantId])

  const merchantOptions = useMemo(() => {
    return merchants.map(m => ({ label: m.name, value: m.id }))
  }, [merchants])

  const buildParams = useCallback(() => {
    if (!dateRange || dateRange.length !== 2) return null
    if (!merchantId) return null
    return {
      date_from: dateRange[0].format('YYYY-MM-DD'),
      date_to: dateRange[1].format('YYYY-MM-DD'),
      merchant_id: merchantId,
    }
  }, [dateRange, merchantId])

  const handleSearch = useCallback(async () => {
    const params = buildParams()
    if (!params) {
      message.warning(t('operator.pages.stocks.location_snapshots.select_filters'))
      return
    }
    setLoading(true)
    try {
      const res = await locationSnapshotService.summary(params)
      if (res.success) {
        setSummaryData(res.data)
      }
    } catch (e) {
      message.error(t('common.error_label'))
    } finally {
      setLoading(false)
    }
  }, [buildParams, t])

  const handleExport = useCallback(async () => {
    const params = buildParams()
    if (!params) {
      message.warning(t('operator.pages.stocks.location_snapshots.select_filters'))
      return
    }
    setExporting(true)
    try {
      const res = await locationSnapshotService.export({ ...params, locale: i18n.language })
      if (res.success && res.data?.job_id) {
        addJob(res.data.job_id, 'operator.pages.stocks.location_snapshots.export_job_name')
        message.success(t('shared.export.messages.start'))
      }
    } catch (e) {
      message.error(t('common.error_label'))
    } finally {
      setExporting(false)
    }
  }, [buildParams, addJob, t, i18n])

  // Total location-days = sum of daily location counts (billing base)
  const totalLocationDays = useMemo(() => {
    if (!summaryData) return 0
    return summaryData.reduce((sum, r) => sum + (r.location_count || 0), 0)
  }, [summaryData])

  // Number of unique dates in the result
  const totalDays = useMemo(() => {
    if (!summaryData) return 0
    return new Set(summaryData.map(r => r.date)).size
  }, [summaryData])

  // Average daily locations
  const avgDailyLocations = useMemo(() => {
    if (!totalDays) return 0
    return Math.round(totalLocationDays / totalDays)
  }, [totalLocationDays, totalDays])

  const totalQuantity = useMemo(() => {
    if (!summaryData) return 0
    return summaryData.reduce((sum, r) => sum + (r.total_quantity || 0), 0)
  }, [summaryData])

  const shelfTypeFilters = useMemo(() => {
    if (!summaryData) return []
    const unique = [...new Map(summaryData.map(r => [r.shelf_type_id, r.shelf_type_name])).entries()]
    return unique.map(([, name]) => ({ text: name, value: name }))
  }, [summaryData])

  // Chart data: pivot rows into { date, [shelf_type_name]: location_count, ... }
  const { chartData, shelfTypeNames } = useMemo(() => {
    if (!summaryData) return { chartData: [], shelfTypeNames: [] }
    const names = [...new Set(summaryData.map(r => r.shelf_type_name))]
    const byDate = {}
    summaryData.forEach(r => {
      if (!byDate[r.date]) byDate[r.date] = { date: r.date }
      byDate[r.date][r.shelf_type_name] = r.location_count
    })
    return { chartData: Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)), shelfTypeNames: names }
  }, [summaryData])

  const columns = [
    {
      title: t('models.location_snapshot.date'),
      dataIndex: 'date',
      key: 'date',
      width: 120,
      sorter: (a, b) => a.date.localeCompare(b.date),
      defaultSortOrder: 'ascend',
    },
    {
      title: t('operator.pages.stocks.location_snapshots.shelf_type'),
      dataIndex: 'shelf_type_name',
      key: 'shelf_type_name',
      filters: shelfTypeFilters,
      onFilter: (value, record) => record.shelf_type_name === value,
    },
    {
      title: t('operator.pages.stocks.location_snapshots.location_count'),
      dataIndex: 'location_count',
      key: 'location_count',
      align: 'right',
      width: 120,
      sorter: (a, b) => a.location_count - b.location_count,
    },
    {
      title: t('operator.pages.stocks.location_snapshots.total_quantity'),
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      align: 'right',
      width: 120,
      sorter: (a, b) => a.total_quantity - b.total_quantity,
    },
  ]

  // Limit date selection to 13 months back
  const disabledDate = useCallback((current) => {
    if (!current) return false
    const thirteenMonthsAgo = dayjs().subtract(13, 'month').startOf('month')
    return current.isBefore(thirteenMonthsAgo, 'day') || current.isAfter(dayjs(), 'day')
  }, [])

  const canSearch = dateRange && dateRange.length === 2 && merchantId

  return (
    <div>
      <ListPageHeader
        items={['stocks', 'stocks.location_snapshots']}
        guideButton={
          <Tooltip title={t('concepts.location_snapshots.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              onClick={() => setConceptModalVisible(true)}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        }
      />
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Filters */}
          <Space wrap>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
              placeholder={[t('common.placeholder.start_date'), t('common.placeholder.end_date')]}
              allowClear={false}
              disabledDate={disabledDate}
              presets={[
                { label: t('operator.pages.stocks.location_snapshots.presets.last_month'), value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                { label: t('operator.pages.stocks.location_snapshots.presets.this_month'), value: [dayjs().startOf('month'), dayjs()] },
              ]}
            />
            <HideInStandard>
              <Select
                value={merchantId}
                onChange={(v) => { setMerchantId(v); setSummaryData(null) }}
                options={merchantOptions}
                style={{ minWidth: 200 }}
                placeholder={t('operator.pages.stocks.location_snapshots.select_merchant')}
                showSearch
                optionFilterProp="label"
              />
            </HideInStandard>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
              disabled={!canSearch}
            >
              {t('operator.pages.stocks.location_snapshots.generate_report')}
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exporting}
              disabled={!canSearch}
            >
              {t('operator.pages.stocks.location_snapshots.export_detail')}
            </Button>
          </Space>

          {/* Summary Statistics */}
          {summaryData && (
            <>
              <Row gutter={[24, 16]}>
                <Col>
                  <Statistic
                    title={t('operator.pages.stocks.location_snapshots.total_location_days')}
                    value={totalLocationDays}
                    suffix={t('operator.pages.stocks.location_snapshots.location_day_unit')}
                  />
                </Col>
                <Col>
                  <Statistic
                    title={t('operator.pages.stocks.location_snapshots.avg_daily_locations')}
                    value={avgDailyLocations}
                  />
                </Col>
                <Col>
                  <Statistic
                    title={t('operator.pages.stocks.location_snapshots.total_units')}
                    value={totalQuantity}
                  />
                </Col>
              </Row>

              {chartData.length > 1 && (
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" fontSize={12} />
                      <YAxis fontSize={12} allowDecimals={false} />
                      <RechartsTooltip />
                      <Legend />
                      {shelfTypeNames.map((name, i) => (
                        <Line
                          key={name}
                          type="monotone"
                          dataKey={name}
                          stroke={LINE_COLORS[i % LINE_COLORS.length]}
                          strokeWidth={2}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              <Table
                columns={columns}
                dataSource={summaryData}
                rowKey={(r) => `${r.date}_${r.shelf_type_id}`}
                pagination={summaryData.length > 50 ? { pageSize: 50 } : false}
                size="middle"
                summary={(pageData) => {
                  const pageLocs = pageData.reduce((s, r) => s + (r.location_count || 0), 0)
                  const pageQty = pageData.reduce((s, r) => s + (r.total_quantity || 0), 0)
                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={2}>
                        <strong>{t('operator.pages.stocks.location_snapshots.page_total')}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <strong>{pageLocs}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        <strong>{pageQty}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )
                }}
              />

            </>
          )}
        </Space>
      </Card>
      <LocationSnapshotConceptModal
        visible={conceptModalVisible}
        onClose={() => setConceptModalVisible(false)}
      />
    </div>
  )
}
